import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Bookmark, RefreshCw, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PowerballBall } from "./PowerballBall";
import { SharePick } from "./SharePick";
import { CouncilPanel } from "./CouncilPanel";

const GHOST = "text-parchment hover:text-oracle hover:bg-white/5 label-caps";

export function OracleResult({ pick, label, onSave, onRegenerate }) {
  const [shareOpen, setShareOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.5 }}
      className="mt-12 relative bg-black/40 backdrop-blur-xl border border-oracle/20 rounded-xl p-6 sm:p-10 shadow-[0_0_40px_rgba(255,176,0,0.1)]"
      data-testid="oracle-result"
    >
      <div className="label-caps text-oracle mb-4 flex items-center justify-center gap-2">
        <Sparkles className="w-3 h-3" strokeWidth={1.5} />
        {label}
        <Sparkles className="w-3 h-3" strokeWidth={1.5} />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
        {pick.whites.map((n, i) => (
          <PowerballBall key={n} n={n} size="lg" testid={`oracle-white-${i}`} delay={i * 90} />
        ))}
        <div className="mx-1 text-parchment/30 font-heading text-3xl">·</div>
        <PowerballBall n={pick.powerball} red size="lg" testid="oracle-powerball" delay={5 * 90} />
      </div>
      <div className="mt-8 max-w-xl mx-auto">
        <p className="font-heading italic text-lg sm:text-xl leading-snug text-parchment/85">&ldquo;{pick.reasoning}&rdquo;</p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <div className="label-caps">Confidence</div>
          <div className="font-body text-oracle text-xl">{(pick.confidence * 100).toFixed(1)}%</div>
          <div className="w-32 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-oracle" style={{ width: `${Math.min(100, pick.confidence * 100)}%` }} />
          </div>
        </div>
      </div>
      {pick.council && <CouncilPanel pick={pick} />}
      <div className="mt-6 max-w-xl mx-auto flex items-center justify-center gap-3">
        <Button data-testid="save-pick-btn" onClick={onSave} variant="ghost" className={GHOST}>
          <Bookmark className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save Pick
        </Button>
        <Button data-testid="share-pick-btn" onClick={() => setShareOpen(true)} variant="ghost" className={GHOST}>
          <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Share
        </Button>
        <Button data-testid="regenerate-btn" onClick={onRegenerate} variant="ghost" className={GHOST}>
          <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} /> Re-cast
        </Button>
      </div>
      <SharePick pick={pick} strategyLabel={label} open={shareOpen} onOpenChange={setShareOpen} />
    </motion.div>
  );
}
